/**
 * ASME B31.3-2022 Table 304.1.1 — "Values of Coefficient Y for t < D/6".
 *
 * Source: ASME B31.3-2022, Table 304.1.1 (Process Piping). Transcribed 2026-08-11.
 * SME sign-off against the licensed source is recommended before production use, as with
 * the allowable-stress catalog (`allowableStress/b313TableA1Data.ts`).
 *
 * Y is a temperature/material-family coefficient in the straight-pipe pressure-design
 * equation. For `t < D/6` it is read from this table; for `t >= D/6` it is computed
 * geometrically — see `resolvePressureCoefficient.ts`.
 */

export type YMaterialFamily =
  | 'ferritic'
  | 'austenitic'
  | 'nickel'
  | 'grayIron'
  | 'otherDuctile'

/**
 * Metal temperatures (°C) tabulated by Table 304.1.1. The first column is "482 °C (900 °F)
 * and below"; the last is "677 °C (1,250 °F) and above". (°F: 900/950/1000/1050/1100/1150/1200/1250.)
 */
export const Y_TEMPS_C = [482, 510, 538, 566, 593, 621, 649, 677] as const

/**
 * Y by material family × metal temperature. `null` = the source lists no value for that
 * family at that temperature (gray/cast iron above 482 °C) — never treated as 0.
 */
export const Y_304_TABLE: Record<YMaterialFamily, readonly (number | null)[]> =
  {
    // 482↓  510   538   566   593   621   649   677↑
    ferritic: [0.4, 0.5, 0.7, 0.7, 0.7, 0.7, 0.7, 0.7],
    austenitic: [0.4, 0.4, 0.4, 0.4, 0.5, 0.7, 0.7, 0.7],
    nickel: [0.4, 0.4, 0.4, 0.4, 0.4, 0.4, 0.5, 0.7],
    grayIron: [0.0, null, null, null, null, null, null, null],
    otherDuctile: [0.4, 0.4, 0.4, 0.4, 0.4, 0.4, 0.4, 0.4],
  }

export const FAMILY_LABELS: Record<YMaterialFamily, string> = {
  ferritic: 'Ferritic steels',
  austenitic: 'Austenitic steels',
  nickel: 'Nickel alloys (UNS N06617, N08800, N08810, N08825)',
  grayIron: 'Gray iron',
  otherDuctile: 'Other ductile metals',
}
